/**
 * Shared import factories for dashboard (and related) routes.
 * Keep this file free of React route trees to avoid circular imports
 * with pages that call prefetch helpers.
 */

export const loadDashboardLayout = () => import("../layouts/dashboard");
export const loadDashboardAppPage = () =>
  import("../pages/dashboard/DashboardAppPage");
export const loadCommunityNotificationsPage = () =>
  import("../pages/dashboard/community/CommunityNotificationsPage");
export const loadOrders = () => import("../pages/orders/Orders");
export const loadSettings = () => import("../pages/settings");
export const loadCatalogPage = () => import("../pages/catalog/CatalogPage");
export const loadProductsPage = () => import("../pages/products/ProductsPage");
export const loadProductPage = () =>
  import("../pages/catalog/detailPages/ProductPage");
export const loadStores = () => import("../pages/store/Stores");
export const loadProfile = () => import("../pages/profile/Profile");
export const loadUserPage = () => import("../pages/users/UserPage");
export const loadCart = () => import("../pages/cart");
export const loadScopes = () => import("../pages/scopes");
export const loadApps = () => import("../pages/apps");
export const loadTrainings = () => import("../pages/trainings");
export const loadTrainingsDetail = () =>
  import("../pages/trainings/TrainingsDetail");
export const loadTutorials = () => import("../pages/tutorials");
export const loadTutorialDetail = () =>
  import("../pages/tutorials/TutorialDetail");
export const loadGrowthMaterial = () => import("../pages/growth-material");
export const loadViewPDF = () => import("../pages/growth-material/ViewPDF");
export const loadMockupLibrary = () => import("../pages/mockup-library");
export const loadShipstationCredentials = () =>
  import("../pages/shipstation/ShipstationCredentials");
export const loadSubscriptionManagement = () =>
  import("../pages/subscription-management");
export const loadBreakdownPage = () => import("../pages/breakdown/BreakdownPage");
export const loadCountdownAdmin = () =>
  import("../pages/countdown-management/CountdownAdmin");
export const loadInvoices = () => import("../pages/orders/Invoices");
export const loadVipStatus = () => import("../pages/vips/VipStatus");

/** Exact path → chunk loader (navbar + common secondary routes). */
const EXACT_LOADERS = {
  "/dashboard/app": loadDashboardAppPage,
  "/dashboard/notifications": loadCommunityNotificationsPage,
  "/dashboard/orders": loadOrders,
  "/dashboard/settings": loadSettings,
  "/dashboard/catalog": loadCatalogPage,
  "/dashboard/products": loadProductsPage,
  "/dashboard/stores": loadStores,
  "/dashboard/profile": loadProfile,
  "/dashboard/user": loadUserPage,
  "/dashboard/checkout": loadCart,
  "/dashboard/scopes": loadScopes,
  "/dashboard/apps": loadApps,
  "/dashboard/trainings": loadTrainings,
  "/dashboard/tutorials": loadTutorials,
  "/dashboard/growth-material": loadGrowthMaterial,
  "/dashboard/mockup-library": loadMockupLibrary,
  "/dashboard/shipstation": loadShipstationCredentials,
  "/dashboard/subscription-management": loadSubscriptionManagement,
  "/dashboard/breakdown": loadBreakdownPage,
  "/dashboard/live-session": loadCountdownAdmin,
  "/dashboard/order-invoice": loadInvoices,
  "/dashboard/vip-stats": loadVipStatus,
  "/dashboard/vip-status": loadVipStatus,
};

const PATTERN_LOADERS = [
  { test: (p) => p.startsWith("/dashboard/trainings/"), load: loadTrainingsDetail },
  { test: (p) => p.startsWith("/dashboard/tutorials/"), load: loadTutorialDetail },
  {
    test: (p) => p.startsWith("/dashboard/growth-material/"),
    load: loadViewPDF,
  },
  { test: (p) => p.startsWith("/dashboard/product/"), load: loadProductPage },
  {
    test: (p) => p.startsWith("/dashboard/products"),
    load: loadProductsPage,
  },
  {
    test: (p) => p.startsWith("/dashboard/catalog"),
    load: loadCatalogPage,
  },
];

function normalizePath(path) {
  if (!path || typeof path !== "string") return "";
  const bare = path.split("?")[0].split("#")[0];
  if (bare.length > 1 && bare.endsWith("/")) return bare.slice(0, -1);
  return bare;
}

function resolveLoader(path) {
  const normalized = normalizePath(path);
  if (!normalized) return null;
  if (EXACT_LOADERS[normalized]) return EXACT_LOADERS[normalized];
  const pattern = PATTERN_LOADERS.find(({ test }) => test(normalized));
  return pattern?.load ?? null;
}

/** Warm a single dashboard route chunk (safe to call repeatedly). */
export function prefetchPath(path) {
  const loader = resolveLoader(path);
  if (!loader) return;
  try {
    loader();
  } catch {
    // Ignore prefetch failures; navigation will retry the import.
  }
}

/** Warm multiple route chunks (e.g. role-visible nav items). */
export function prefetchPaths(paths = []) {
  paths.forEach(prefetchPath);
}

/**
 * Prefetch post-login shell so navigate("/dashboard/app") is near-instant.
 * Also used from login / marketing CTAs.
 */
export function prefetchDashboardRoutes() {
  loadDashboardLayout();
  loadDashboardAppPage();
  loadOrders();
  loadSettings();
  loadCatalogPage();
  loadProductsPage();
  loadProductPage();
}
